import { type FC, useState } from "react";

import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { t } from "i18n/i18n";
import { Chip } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import { AssessPointsMarkerIcon, ChevronDownIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

interface PointsEditorProps {
  buttonLabel: string;
  buttonLabelShort?: string;
  containerQueryClassNameHide?: string;
  containerQueryClassNameShow?: string;
  disabled?: boolean;
  eventName?: string;
  hideIcon?: boolean;
  initialPoints: number;
  inputMax?: string | number;
  inputStep?: string | number;
  inputTitle: string;
  onSave: (points: number) => void;
  pageId: string;
  parseInput: (input: string) => { value?: number; error?: string };
  readOnly?: boolean;
  saveTextKey?: "save" | "update";
  tooltipText?: string;
}

export const PointsEditor: FC<PointsEditorProps> = ({
  readOnly = false,
  initialPoints,
  ...props
}) => {
  if (readOnly) {
    return (
      <Chip kind="white">
        <IconText
          containerQueryClassNameHide={props.containerQueryClassNameHide}
          containerQueryClassNameShow={props.containerQueryClassNameShow}
          iconStart={props.hideIcon ? undefined : <AssessPointsMarkerIcon />}
          shortText={props.buttonLabelShort}
          text={props.buttonLabel}
        />
      </Chip>
    );
  }

  // Remount when the saved points change so the input picks up pubsub updates.
  return <PointsEditorMenu initialPoints={initialPoints} key={initialPoints} {...props} />;
};

const PointsEditorMenu: FC<Omit<PointsEditorProps, "readOnly">> = ({
  buttonLabel,
  buttonLabelShort,
  containerQueryClassNameHide,
  containerQueryClassNameShow,
  disabled = false,
  eventName,
  hideIcon = false,
  initialPoints,
  inputMax,
  inputStep,
  inputTitle,
  onSave,
  pageId,
  parseInput,
  saveTextKey,
  tooltipText = "",
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["title"]);
  const { canAuthorSection } = useCapabilities();
  const [inputValue, setInputValue] = useState(String(initialPoints));
  const { value: parsedPoints, error: errorMessage } = parseInput(inputValue);

  return (
    <Tooltip content={disabled ? tooltipText : ""}>
      <CancelSaveMenu
        button={
          <Chip as="button" disabled={disabled} kind="white" title={!disabled ? tooltipText : ""}>
            <IconText
              containerQueryClassNameHide={containerQueryClassNameHide}
              containerQueryClassNameShow={containerQueryClassNameShow}
              iconEnd={canAuthorSection && !disabled && <ChevronDownIcon />}
              iconStart={hideIcon ? undefined : <AssessPointsMarkerIcon />}
              shortText={buttonLabelShort}
              text={buttonLabel}
            />
          </Chip>
        }
        isSaveDisabled={parsedPoints === undefined || errorMessage !== undefined}
        saveTextKey={saveTextKey}
        title={t("glossary.points")}
        onCancel={() => setInputValue(String(initialPoints))}
        onSave={() => {
          if (parsedPoints !== undefined) onSave(parsedPoints);
        }}
      >
        <div className="flex w-full flex-col">
          <TextField
            eventName={eventName}
            eventProperties={{ pageTitle: page?.title, pageId }}
            iconStart={<AssessPointsMarkerIcon />}
            max={inputMax}
            min="0"
            placeholder={t("rubric_card.assessment_points", { count: 0 })}
            step={inputStep}
            title={inputTitle}
            type="number"
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
          />

          {errorMessage && <div className="body-xs mt-1 flex w-full text-red">{errorMessage}</div>}
        </div>
      </CancelSaveMenu>
    </Tooltip>
  );
};
